import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PortalPageHeader } from "@/components/portal-page-header";
import { buildDataTableQueryOptions, ensureRouteQueryData } from "@/lib/route-loader";
import { searchStringSearchSchema } from "@/lib/route-search";
import { OrganizationFormResponseStatus, type ParentOrganizationFormListItemDTO } from "@/orval/model";
import {
  getParentOrganizationFormsGetAvailableFormsQueryKey,
  getParentOrganizationFormsGetAvailableFormsQueryOptions,
  parentOrganizationFormsGetAvailableForms,
} from "@/orval/parent-organization-forms";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { PlayIcon } from "lucide-react";

function FormResponseStatusBadge({ form }: { form: ParentOrganizationFormListItemDTO }) {
  if (form.responseStatus === OrganizationFormResponseStatus.Submitted) {
    return (
      <Badge className="w-fit whitespace-nowrap border-0 bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/50 dark:text-green-300 dark:hover:bg-green-900/50">
        Submitted
      </Badge>
    );
  }
  if (form.responseStatus === OrganizationFormResponseStatus.Draft) {
    return (
      <Badge className="w-fit whitespace-nowrap border-0 bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/30">
        In progress
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="w-fit whitespace-nowrap">
      New
    </Badge>
  );
}

const columns = (organizationId: number): ColumnDef<ParentOrganizationFormListItemDTO>[] => [
  { header: ({ column }) => <DataTableColumnHeader column={column} title="Form" />, accessorKey: "name" },
  {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    id: "status",
    size: 150,
    cell: ({ row }) => <FormResponseStatusBadge form={row.original} />,
  },
  {
    header: "",
    id: "actions",
    size: 168,
    cell: ({ row }) => (
      <Button
        size="sm"
        variant={row.original.responseStatus === OrganizationFormResponseStatus.Submitted ? "outline" : "default"}
        asChild
      >
        <Link
          to="/organizations/$organizationId/parent/forms/$formId"
          params={{ organizationId, formId: row.original.id }}
        >
          <FormActionLabel status={row.original.responseStatus} />
        </Link>
      </Button>
    ),
  },
];

function FormActionLabel({ status }: { status: ParentOrganizationFormListItemDTO["responseStatus"] }) {
  if (status === OrganizationFormResponseStatus.Submitted) {
    return "View form";
  }
  if (status === OrganizationFormResponseStatus.Draft) {
    return "Resume form";
  }
  return (
    <>
      <PlayIcon className="mr-2 size-4" />
      Start form
    </>
  );
}

function ParentFormsPage() {
  const { organizationId } = Route.useParams();
  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6 [&_thead_tr]:top-0 [&_table]:h-auto">
      <PortalPageHeader
        className="mb-8"
        title="Forms"
        subtitle="Complete forms for your family. Use Save draft to keep your progress before leaving."
      />
      <DataTable
        contentLayout
        columns={columns(organizationId)}
        queryFn={(params, signal) => parentOrganizationFormsGetAvailableForms(organizationId, params, signal)}
        queryKeyFn={(params) => getParentOrganizationFormsGetAvailableFormsQueryKey(organizationId, params)}
        rowLink={(row) => ({
          to: "/organizations/$organizationId/parent/forms/$formId",
          params: { organizationId, formId: row.id },
        })}
        getRowIdFn={(row) => row.id.toString()}
      />
    </div>
  );
}

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/forms")({
  validateSearch: searchStringSearchSchema,
  loader: ({ params, location, ...loaderContext }) =>
    ensureRouteQueryData(
      loaderContext,
      getParentOrganizationFormsGetAvailableFormsQueryOptions(
        params.organizationId,
        buildDataTableQueryOptions(searchStringSearchSchema.parse(location.search)),
      ),
    ),
  component: ParentFormsPage,
});
